// Lightweight calendar helpers for the slot picker. Pure functions, no deps —
// the public widget ships without a date library on purpose. Everything works
// on local YYYY-MM-DD strings to stay timezone-safe (no UTC round-trips).

import { WEEKDAYS } from './constants';

export interface DayCell {
  iso: string; // YYYY-MM-DD
  day: number; // 1..31
  inMonth: boolean; // belongs to the displayed month (vs. leading/trailing padding)
}

const pad = (n: number) => String(n).padStart(2, '0');

// month is 0-based (JS Date convention).
export const isoOf = (year: number, month: number, day: number): string =>
  `${year}-${pad(month + 1)}-${pad(day)}`;

// Today as a local YYYY-MM-DD (no timezone conversion).
export function todayISO(): string {
  const d = new Date();
  return isoOf(d.getFullYear(), d.getMonth(), d.getDate());
}

// Year + 0-based month of an ISO date string.
export function ymOf(iso: string): { year: number; month: number } {
  const [y, m] = iso.split('-').map(Number);
  return { year: y, month: m - 1 };
}

// Whole-month ordinal — used to clamp month navigation to the available range.
export const monthIndex = (year: number, month: number): number => year * 12 + month;

// Mon-first 6×7 grid for the given month. Always 42 cells so the grid height
// stays stable across months (no layout jump when paging).
export function monthMatrix(year: number, month: number): DayCell[] {
  const first = new Date(year, month, 1);
  // JS getDay(): 0=Sun … 6=Sat → shift so Monday is column 0.
  const lead = (first.getDay() + 6) % 7;
  const cells: DayCell[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(year, month, 1 - lead + i);
    cells.push({
      iso: isoOf(d.getFullYear(), d.getMonth(), d.getDate()),
      day: d.getDate(),
      inMonth: d.getMonth() === month,
    });
  }
  return cells;
}

// "Montag, 15.06." — long weekday + short date, German.
export function formatDayLabel(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  const wd = WEEKDAYS[d.getDay() === 0 ? 7 : d.getDay()];
  return `${wd}, ${d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}`;
}

export const MONTH_NAMES = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];

export const WEEKDAY_SHORT = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
